// Bed-day accrual: registers and moves the recurring `ipd_active_service` row
// that the daily auto-charge job bills from. The endpoints are thin passthroughs
// to the charge-capture service (BE: main/ipd/manage_ipd_billing_auto_charge.py,
// routed at main/urls.py:791-793).
//
// ADR 0009 rules that charges post server-side as a side effect of the clinical
// event. This module is a deliberate, scope-driven deviation (frontend-only
// task) and is therefore the ONLY place that decides accrual policy — call sites
// call an orchestrator and render its result, never the transport functions.
// See docs/superpowers/specs/2026-08-04-bed-day-accrual-fe-integration-design.md

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

function servicesUrl(admissionId) {
  return `${BASE}ipd_admission/${admissionId}/billing/auto-charges/active-services/`;
}

// ── Transport ──────────────────────────────────────────────────────────────
// Named to be unmistakably distinct from the orchestrators below, so a call site
// cannot reach for one of these by accident and skip the policy layer.

export async function fetchActiveServices(admissionId) {
  const res = await axios.get(servicesUrl(admissionId), { headers: authHeaders() });
  return res?.data;
}

export async function postActiveServiceStart(admissionId) {
  const res = await axios.post(
    `${servicesUrl(admissionId)}start/`,
    { charge_kind: "bed_day" },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// end_time is omitted by JSON.stringify when undefined; the backend then defaults
// it to server now() (billing_ipd_auto_charge_sync.py:186-188).
export async function postActiveServiceStop(
  admissionId,
  serviceId,
  { stopReason, endTime } = {}
) {
  const res = await axios.post(
    `${servicesUrl(admissionId)}${serviceId}/stop/`,
    { stop_reason: stopReason, end_time: endTime },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return res?.data;
}

// ── Picking the bed service out of the list ────────────────────────────────
// The list returns every active recurring service for the admission — bed,
// nursing, ventilator, oxygen. The bed one is the row the start payload stamped
// service_type=BED / category=ROOM_RENT (BE:
// billing_ipd_auto_charge_sync.py:104-107).

export function pickBedServices(activeServices) {
  const rows = Array.isArray(activeServices) ? activeServices : [];
  const byType = rows.filter(
    (s) => String(s?.service_type || "").toUpperCase() === "BED"
  );
  if (byType.length) return byType;
  return rows.filter(
    (s) => String(s?.category || "").toUpperCase() === "ROOM_RENT"
  );
}

// The id field name originates in the external charge-capture service, which is
// in neither repo. Task 4 of the implementation plan pins it against manage-dev
// and collapses this to the real field.
export function serviceIdOf(svc) {
  return svc?.id ?? svc?.service_id ?? svc?.active_service_id ?? null;
}

// ── Presenter ──────────────────────────────────────────────────────────────
// Mirrors describeBedCharge() in bedTransferRequestApi.js:27-52 so the two
// billing messages read alike at every call site.

export function describeAccrual(result) {
  const detail = result?.detail ? ` (${result.detail})` : "";
  switch (result?.state) {
    case "disabled":
      return null;
    case "started":
      return { severity: "success", text: "Daily bed charge started." };
    case "already-running":
      return { severity: "success", text: "Daily bed charge was already running." };
    case "moved":
      return { severity: "success", text: "Daily bed charge moved to the new bed." };
    case "started-without-previous":
      return {
        severity: "warning",
        text:
          "Daily bed charge started, but no accrual was found on the previous " +
          "bed — check this stay was not left unbilled before now.",
      };
    case "stop-failed":
      return {
        severity: "warning",
        text:
          `The bed moved, but the daily bed charge is STILL ON THE OLD BED${detail}. ` +
          "Stop it and start one on the new bed by hand.",
      };
    case "stopped-not-restarted":
      return {
        severity: "warning",
        text:
          `The bed moved and the daily bed charge was STOPPED but NOT restarted${detail}. ` +
          "This patient is accruing no bed charge — start it by hand.",
      };
    case "start-failed":
      return {
        severity: "warning",
        text:
          `No daily bed charge was started for this patient${detail}. ` +
          "Start it by hand, or this stay will not be billed for the bed.",
      };
    default:
      return null;
  }
}

// ── Orchestrators ──────────────────────────────────────────────────────────
// The only functions call sites should use. Every accrual policy decision —
// stop/start ordering, reason codes, what counts as success — lives here so no
// surface re-derives it.

function errText(err) {
  return err?.response?.data?.message || err?.message || null;
}

function isAlreadyRunning(message) {
  return String(message || "")
    .toLowerCase()
    .includes("already running");
}

export async function startBedAccrual(admissionId) {
  try {
    const data = await postActiveServiceStart(admissionId);
    if (data?.status === "Success") {
      // Success with no active_service means ipd_auto_charge_enabled() is off
      // backend-side — a no-op, not a failure.
      return data?.active_service
        ? { state: "started", detail: null }
        : { state: "disabled", detail: null };
    }
    if (isAlreadyRunning(data?.message)) {
      return { state: "already-running", detail: null };
    }
    return { state: "start-failed", detail: data?.message || null };
  } catch (err) {
    return { state: "start-failed", detail: errText(err) };
  }
}

export async function moveBedAccrual(admissionId) {
  let existing = [];
  try {
    const list = await fetchActiveServices(admissionId);
    if (list?.status !== "Success") {
      return { state: "stop-failed", detail: list?.message || null };
    }
    existing = pickBedServices(list?.active_services);
  } catch (err) {
    return { state: "stop-failed", detail: errText(err) };
  }

  // More than one active bed service is already a double-billing bug; stop them
  // all rather than leave the extras running.
  for (const svc of existing) {
    const serviceId = serviceIdOf(svc);
    if (!serviceId) {
      return {
        state: "stop-failed",
        detail: "active service id missing from the list response",
      };
    }
    try {
      const stopped = await postActiveServiceStop(admissionId, serviceId, {
        stopReason: "TRANSFER",
      });
      if (stopped?.status !== "Success") {
        return { state: "stop-failed", detail: stopped?.message || null };
      }
    } catch (err) {
      return { state: "stop-failed", detail: errText(err) };
    }
  }

  const started = await startBedAccrual(admissionId);
  if (started.state === "disabled") return { state: "disabled", detail: null };
  if (started.state === "started" || started.state === "already-running") {
    return existing.length
      ? { state: "moved", detail: null }
      : { state: "started-without-previous", detail: null };
  }
  // The stop landed but the start did not: the patient is in a bed accruing
  // nothing. This is the case that must never be quiet.
  return existing.length
    ? { state: "stopped-not-restarted", detail: started.detail }
    : { state: "start-failed", detail: started.detail };
}
